<template>
  <div class="content-wrap">
    <Notifications />
    <h2>Royalty Statements</h2>
    <div
      v-if="hideStatements()"
      class="wrap-table border-table"
    >
      Please choose a practice first.
    </div>
    <div v-else>  
      <div v-if="showPeriodFilter" class="d-flex justify-content-end mb-1 mt-md-4 pb-2">
        <div class="d-flex flex-wrap">
          <div class="filter-group my-1">
            <label for="filter_data">View royalties detail for:</label>
            <select
              v-model="year"
              class="select-xs"
            >
              <option
                v-for="i in periodOption"
                :key="i"
                :value="i"
              >
                {{ i }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div style="overflow-x: auto; width: 100%;">
        <table class="table table-box table-striped statements-table">
          <thead>
            <tr>
              <th>Sale Type</th>
              <th>Month</th>
              <th class="t-align">Clients</th>
              <th class="t-align">Orders</th>
              <th class="t-align">Avg Order</th>
              <th class="t-align">Total Order</th>
              <th class="t-align">Total Royalty</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="sortedNewPurchases.length === 0">
              <td colspan="7" class="text-center pt-3">No new purchase logs found.</td>
            </tr>
            <tr v-for="(data, index) in sortedNewPurchases" :key="'np-' + index">
              <td>{{ index===0 ? data.sale_type : '' }}</td>
              
              <td v-if="data.royalty_history_id">
                <a
                  :href="`/api/royalty_histories/${data.royalty_history_id}.xlsx`"
                  class="link_action"
                >
                  {{ data.month }}
                </a>
              </td>
              <td v-else>
                {{ data.month }}
              </td>

              
              <td class="t-align">{{ data.clients }}</td>
              <td class="t-align">{{ data.orders }}</td>
              <td class="t-align">${{ formatMoney(data.avg_order) }}</td>
              <td class="t-align">${{ formatMoney(data.total_order) }}</td>
              <td class="t-align">${{ formatMoney(data.total_royalty) }}</td>
            </tr>
            <!-- Total row -->
            <tr class="highlight">
              <td>New Purchase Total</td>
              <td></td>
              <td class="t-align">{{ getTotal('New Purchase', 'clients', 0) }}</td>
              <td class="t-align">{{ getTotal('New Purchase', 'orders', 0) }}</td>
              <td class="t-align">${{ formatMoney(getTotal('New Purchase', 'avg_order')) }}</td>
              <td class="t-align">${{ formatMoney(getTotal('New Purchase', 'total_order')) }}</td>
              <td class="t-align">${{ formatMoney(getTotal('New Purchase', 'total_royalty')) }}</td>
            </tr>
        
            <tr v-if="sortedRefills.length === 0">
              <td colspan="7" class="text-center pt-3">No refill logs found.</td>
            </tr>
            <tr v-for="(data, index) in sortedRefills" :key="'refill-' + index">
              <td>{{ index===0 ? data.sale_type : '' }}</td>
              <td v-if="data.royalty_history_id">
                <a
                  :href="`/api/royalty_histories/${data.royalty_history_id}.xlsx`"
                  class="link_action"
                >
                  {{ data.month }}
                </a>
              </td>
              <td v-else>
                {{ data.month }}
              </td>
              <td class="t-align">{{ data.clients }}</td>
              <td class="t-align">{{ data.orders }}</td>
              <td class="t-align">${{ formatMoney(data.avg_order) }}</td>
              <td class="t-align">${{ formatMoney(data.total_order) }}</td>
              <td class="t-align">${{ formatMoney(data.total_royalty) }}</td>
            </tr>
            <!-- Total row -->
            <tr class="highlight">
              <td>Refill Total</td>
              <td></td>
              <td class="t-align">{{ getTotal('Refill', 'clients', 0) }}</td>
              <td class="t-align">{{ getTotal('Refill', 'orders', 0) }}</td>
              <td class="t-align">${{ formatMoney(getTotal('Refill', 'avg_order')) }}</td>
              <td class="t-align">${{ formatMoney(getTotal('Refill', 'total_order')) }}</td>
              <td class="t-align">${{ formatMoney(getTotal('Refill', 'total_royalty')) }}</td>
            </tr>
            <tr class="text-bold">
              <td>Grand Total</td>
              <td></td>
              <td class="t-align">{{ getTotal('all', 'clients', 0) }}</td>
              <td class="t-align">{{ getTotal('all', 'orders', 0) }}</td>
              <td class="t-align">${{ formatMoney(getTotal('all', 'avg_order')) }}</td>
              <td class="t-align">${{ formatMoney(getTotal('all', 'total_order')) }}</td>
              <td class="t-align">${{ formatMoney(getTotal('all', 'total_royalty')) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>


<script>
import { mapGetters } from 'vuex';
import { ReportService } from '@/services/api.service';
import { encodeQueryData, formatMoney } from '@/helper';

// @vuese
// @group MyAccount
export default {
  name: 'RoyaltyStatement',

  props: {
    // Varable to get practice ID for super admin user
    practiceId: {
      type: [Number, String],
      default: '',
    },
    // Araay of all practices loaded for super admin user
    practices: {
      type: Array,
      default() {
        return [];
      }
    }
  },

  data() {
    return {
      sending: false,
      periodOption: [],
      statements: [],
      year: new Date().getFullYear()
    };
  },

  beforeMount() {
    this.fetchStatements();
  },

  computed: {
    sortedNewPurchases() {
      return this.statements
        .filter(item => item.sale_type === 'New Purchase')
        .sort((a, b) => new Date(b.sale_date) - new Date(a.sale_date));
    },
    sortedRefills() {
      return this.statements
        .filter(item => item.sale_type === 'Refill')
        .sort((a, b) => new Date(b.sale_date) - new Date(a.sale_date));
    },
    showPeriodFilter() {
      return this.periodOption.length > 1;
    }
  },

  watch: {
    year(newYear) {
      this.fetchStatements(newYear);
    }
  },

  methods: {
    encodeQueryData,

    fetchStatements(year = this.year) {
      if (this.sending) return;

      this.sending = true;

      return ReportService.getStatements(this.encodeQueryData({year: year, practice_id: this.practiceId}))
      .then(resp => {
        this.statements = resp.data.statements;
        this.periodOption = resp.data.available_years;
      })
      .finally(() => this.sending = false);
    },

    getTotal(saleType, column, decimals = 2) {
      let relevantRows = [];

      if (saleType === 'all') {
        relevantRows = this.statements;
      } else {
        relevantRows = this.statements.filter(item => item.sale_type === saleType);
      }

      const total = relevantRows.reduce((sum, item) => {
        return sum + parseFloat(item[column] || 0);
      }, 0);

      return decimals === 0 ? Math.round(total) : total.toFixed(decimals);
    },

    formatMoney (val) {
      return formatMoney(val);
    },

    hideStatements() {
      return (this.isRole('super_admin') || this.isRole('group_admin') || this.isRole('group_practice_admin')) && (this.practiceId === '' || this.practiceId === null || this.practiceId === undefined);
    }
  }
};
</script>

<style lang="scss">
  .statements-table {
    .t-align{
      text-align: right;
    }
    tbody {
      tr {
        background-color: white !important;
      }

      tr.highlight {
        background-color: #a989de !important;
        font-weight: bold;
        
      }
    }
  }
</style>
